import React from 'react'
import {
  Upload, Download, FileSpreadsheet, Info, AlertTriangle, AlertCircle,
  Loader2, Check, X
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ImportPreviewData } from '@/utils/templateImportUtils'

interface TemplateImportDialogProps {
  importDialogOpen: boolean
  setImportDialogOpen: (open: boolean) => void
  importStep: 'upload' | 'preview' | 'importing' | 'complete'
  setImportStep: (step: 'upload' | 'preview' | 'importing' | 'complete') => void
  importFile: File | null
  importPreview: ImportPreviewData | null
  importProgress: { current: number; total: number }
  importResults: { success: number; failed: number; errors: string[] }
  fileInputRef: React.RefObject<HTMLInputElement>
  handleCloseImportDialog: () => void
  handleFileDrop: (e: React.DragEvent<HTMLDivElement>) => void
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleDownloadTemplate: () => void
  handleImportTemplates: () => void
  updateExistingTemplates?: boolean
  setUpdateExistingTemplates?: (value: boolean) => void
}

const TemplateImportDialog: React.FC<TemplateImportDialogProps> = ({
  importDialogOpen,
  setImportDialogOpen,
  importStep,
  setImportStep,
  importFile,
  importPreview,
  importProgress,
  importResults,
  fileInputRef,
  handleCloseImportDialog,
  handleFileDrop,
  handleFileInputChange,
  handleDownloadTemplate,
  handleImportTemplates,
  updateExistingTemplates,
  setUpdateExistingTemplates,
}) => {
  return (
    <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            Import Objective Templates
          </DialogTitle>
          <DialogDescription>
            Upload an Excel (.xlsx) file containing template definitions with objectives and KPIs.
            The template includes dropdown lists for L1-L3 strategic alignment.
          </DialogDescription>
        </DialogHeader>

        {/* Upload Step */}
        {importStep === 'upload' && (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-blue-900">How to use the Excel Template:</p>
                  <ul className="list-disc list-inside text-blue-800 space-y-1">
                    <li><strong>Required:</strong> template_name, objective_title - use dropdown for kpa_name</li>
                    <li><strong>Strategic alignment:</strong> Select L1 Pillar, L2 Initiative, L3 Initiative from dropdowns</li>
                    <li><strong>KPA:</strong> Select from dropdown (Operating & Financial, Internal Business Processes, etc.)</li>
                    <li><strong>Template info:</strong> template_description, template_type, role_title</li>
                    <li><strong>Dates:</strong> start_date and end_date (YYYY-MM-DD format)</li>
                    <li><strong>KPIs:</strong> kpi_name, kpi_description, kpi_unit, kpi_target_value, kpi_frequency</li>
                  </ul>
                  <p className="text-blue-700 mt-2">
                    <strong>Tip:</strong> The Excel template has dropdown lists pre-configured. Just select values from the dropdowns.
                  </p>
                </div>
              </div>
            </div>

            {/* Download Template Button */}
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
                <Download className="h-4 w-4" />
                Download Excel Template
              </Button>
            </div>

            {/* Drop Zone */}
            <div
              className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer"
              onDrop={handleFileDrop}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-700 mb-2">
                Drop your Excel file here or click to browse
              </p>
              <p className="text-sm text-slate-500">
                Supports .xlsx files up to 10MB
              </p>
              {importFile && (
                <div className="mt-4 inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
                  <FileSpreadsheet className="h-4 w-4" />
                  {importFile.name}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Preview Step */}
        {importStep === 'preview' && importPreview && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-900">{importPreview.templates.length}</div>
                <div className="text-sm text-blue-700">Templates</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-900">
                  {importPreview.templates.reduce((sum, t) => sum + t.objectives.length, 0)}
                </div>
                <div className="text-sm text-green-700">Objectives</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-900">
                  {importPreview.templates.reduce((sum, t) =>
                    sum + t.objectives.reduce((objSum, o) => objSum + o.kpis.length, 0), 0
                  )}
                </div>
                <div className="text-sm text-purple-700">KPIs</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-slate-900">{importPreview.validRows}/{importPreview.totalRows}</div>
                <div className="text-sm text-slate-700">Valid Rows</div>
              </div>
            </div>

            {/* Errors and Warnings */}
            {importPreview.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  {importPreview.errors.length} Error{importPreview.errors.length !== 1 ? 's' : ''} Found
                </div>
                <ScrollArea className="h-32">
                  <ul className="text-sm text-red-700 space-y-1">
                    {importPreview.errors.slice(0, 20).map((error, i) => (
                      <li key={i}>Row {error.row}: {error.message}</li>
                    ))}
                    {importPreview.errors.length > 20 && (
                      <li className="font-medium">... and {importPreview.errors.length - 20} more errors</li>
                    )}
                  </ul>
                </ScrollArea>
              </div>
            )}

            {importPreview.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                  <AlertCircle className="h-4 w-4" />
                  {importPreview.warnings.length} Warning{importPreview.warnings.length !== 1 ? 's' : ''}
                </div>
                <ScrollArea className="h-24">
                  <ul className="text-sm text-amber-700 space-y-1">
                    {importPreview.warnings.slice(0, 10).map((warning, i) => (
                      <li key={i}>Row {warning.row}: {warning.message}</li>
                    ))}
                    {importPreview.warnings.length > 10 && (
                      <li className="font-medium">... and {importPreview.warnings.length - 10} more warnings</li>
                    )}
                  </ul>
                </ScrollArea>
              </div>
            )}

            {/* Template Preview */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Templates to Import:</h4>
              <ScrollArea className="h-[300px] border rounded-lg">
                <div className="p-4 space-y-4">
                  {importPreview.templates.map((template, tIndex) => (
                    <Card key={tIndex} className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h5 className="font-semibold text-slate-900">{template.name}</h5>
                            <p className="text-sm text-slate-600">{template.description}</p>
                          </div>
                          <div className="flex gap-2">
                            <Badge variant="outline">{template.template_type}</Badge>
                            {template.role_type && <Badge variant="outline">{template.role_type}</Badge>}
                          </div>
                        </div>

                        <div className="space-y-2">
                          {template.objectives.map((obj, oIndex) => (
                            <div key={oIndex} className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <p className="font-medium text-slate-800 text-sm">{obj.title}</p>
                                  <p className="text-xs text-slate-600 mt-1">{obj.description}</p>
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {obj.pillar_name && (
                                      <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700">
                                        L1: {obj.pillar_name}
                                      </Badge>
                                    )}
                                    {obj.strategic_objective_name && (
                                      <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700">
                                        L2: {obj.strategic_objective_name}
                                      </Badge>
                                    )}
                                    {obj.l3_operational_initiative_name && (
                                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                        L3: {obj.l3_operational_initiative_name}
                                      </Badge>
                                    )}
                                    {obj.kpa_name && (
                                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
                                        KPA: {obj.kpa_name}
                                      </Badge>
                                    )}
                                    <Badge variant="outline" className="text-xs">
                                      {obj.kpis.length} KPI{obj.kpis.length !== 1 ? 's' : ''}
                                    </Badge>
                                  </div>
                                </div>
                              </div>

                              {obj.kpis.length > 0 && (
                                <div className="mt-2 pl-3 border-l-2 border-slate-200">
                                  <p className="text-xs font-medium text-slate-500 mb-1">KPIs:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {obj.kpis.map((kpi, kIndex) => (
                                      <Badge key={kIndex} variant="outline" className="text-xs bg-slate-100 text-slate-700 border-slate-300">
                                        {kpi.name} ({kpi.target_value} {kpi.unit})
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Importing Step */}
        {importStep === 'importing' && (
          <div className="py-12 text-center space-y-6">
            <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Importing Templates...</h3>
              <p className="text-slate-600">
                Processing template {importProgress.current} of {importProgress.total}
              </p>
            </div>
            <div className="max-w-md mx-auto">
              <div className="bg-slate-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-blue-600 h-full transition-all duration-300"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                />
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {Math.round((importProgress.current / importProgress.total) * 100)}% complete
              </p>
            </div>
          </div>
        )}

        {/* Complete Step */}
        {importStep === 'complete' && (
          <div className="py-8 space-y-6">
            <div className="text-center">
              {importResults.failed === 0 ? (
                <>
                  <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Import Complete!</h3>
                  <p className="text-slate-600">
                    Successfully imported {importResults.success} template{importResults.success !== 1 ? 's' : ''}
                  </p>
                </>
              ) : importResults.success > 0 ? (
                <>
                  <div className="h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="h-8 w-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Import Partially Complete</h3>
                  <p className="text-slate-600">
                    {importResults.success} template{importResults.success !== 1 ? 's' : ''} imported, {importResults.failed} failed
                  </p>
                </>
              ) : (
                <>
                  <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Import Failed</h3>
                  <p className="text-slate-600">
                    No templates could be imported
                  </p>
                </>
              )}
            </div>

            {/* Results Summary */}
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-900">{importResults.success}</div>
                <div className="text-sm text-green-700">Imported</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-amber-900">{(importResults as any).skipped || 0}</div>
                <div className="text-sm text-amber-700">Skipped</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-900">{importResults.failed}</div>
                <div className="text-sm text-red-700">Failed</div>
              </div>
            </div>

            {/* Error Details */}
            {importResults.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2">Error Details:</h4>
                <ScrollArea className="h-32">
                  <ul className="text-sm text-red-700 space-y-1">
                    {importResults.errors.map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </ScrollArea>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {importStep === 'upload' && (
            <Button variant="outline" onClick={handleCloseImportDialog}>
              Cancel
            </Button>
          )}

          {importStep === 'preview' && (
            <>
              {setUpdateExistingTemplates && (
                <label className="flex items-center gap-2 text-sm mr-auto">
                  <input
                    type="checkbox"
                    checked={updateExistingTemplates}
                    onChange={(e) => setUpdateExistingTemplates(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Update existing templates
                </label>
              )}
              <Button variant="outline" onClick={() => setImportStep('upload')}>
                Back
              </Button>
              <Button
                onClick={handleImportTemplates}
                disabled={importPreview?.templates.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import {importPreview?.templates.length} Template{importPreview?.templates.length !== 1 ? 's' : ''}
              </Button>
            </>
          )}

          {importStep === 'complete' && (
            <>
              {importResults.failed > 0 && (
                <Button variant="outline" onClick={() => setImportStep('upload')}>
                  Try Again
                </Button>
              )}
              <Button onClick={handleCloseImportDialog}>
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TemplateImportDialog
